<template>
    <div class="col-md-12">
        <div class="d-flex align-items-center justify-content-between">
            <div>
                <span class="h2 align-middle">Cockpit</span>
                <span v-if="counters.open_warnings" class="badge badge-danger align-middle ml-2">
                    {{ counters.open_warnings }} warnings
                </span>
                <div class="text-muted">
                    Bookings that break one or more rules and do not match the expected state.
                </div>
            </div>
            <a href="/cockpit/configuration" class="btn btn-outline-secondary">
                <span class="material-icons-outlined align-middle mr-1" style="font-size: 18px;">tune</span>
                Cockpit configuration
            </a>
        </div>

        <div class="flash-message">
            <FlashMessage :position="'top'"></FlashMessage>
        </div>

        <div class="row mt-4">
            <div class="col-md-3" v-for="card in kpiCards" :key="card.tab">
                <div class="card mb-3"
                     :class="{'border-primary': tab === card.tab}"
                     :style="card.tab ? 'cursor: pointer;' : ''"
                     @click="card.tab && switchTab(card.tab)">
                    <div class="card-body py-3">
                        <div class="text-muted text-uppercase" style="font-size: 0.75rem;">{{ card.label }}</div>
                        <div class="h3 my-1" :class="card.textClass">{{ card.value }}</div>
                    </div>
                </div>
            </div>
        </div>

        <div class="row">
            <div class="col-md-3">
                <div class="card">
                    <div class="card-body p-2">
                        <div class="text-muted text-uppercase px-2 py-1" style="font-size: 0.75rem;">Rules</div>
                        <button class="list-group-item list-group-item-action border-0 d-flex justify-content-between align-items-center"
                                :class="{'active': !ruleFilter}"
                                @click="setRuleFilter(null)">
                            All warnings
                        </button>
                        <button v-for="rule in rules" :key="rule.id"
                                class="list-group-item list-group-item-action border-0 d-flex justify-content-between align-items-center"
                                :class="{'active': ruleFilter === rule.id, 'text-muted': !rule.enabled}"
                                @click="setRuleFilter(rule.id)">
                            <span>{{ rule.name }}</span>
                            <span class="badge"
                                  :class="rule.open_warnings > 0 ? 'badge-danger' : 'badge-light'">
                                {{ rule.open_warnings }}
                            </span>
                        </button>
                    </div>
                </div>
            </div>

            <div class="col-md-9">
                <div class="card mb-3">
                    <div class="card-body py-2 d-flex align-items-center">
                        <input type="text"
                               class="form-control mr-3"
                               style="max-width: 340px;"
                               placeholder="Search booking, customer, address..."
                               v-model="search"
                               @input="debouncedFetch">
                        <select class="form-control mr-3" style="max-width: 160px;" v-model="bookingType" @change="fetchWarnings(1)">
                            <option :value="0">All types</option>
                            <option :value="3">Flex</option>
                            <option :value="4">Ad-hoc</option>
                        </select>
                        <div class="ml-auto text-muted">
                            Showing <b>{{ rows.length }}</b> of {{ total }}
                        </div>
                    </div>
                </div>

                <div class="card">
                    <table class="table table-striped mb-0" aria-label="Cockpit warnings table">
                        <thead>
                            <tr>
                                <th style="width: 10%;">Booking</th>
                                <th style="width: 14%;">Customer</th>
                                <th style="width: 8%;">Type</th>
                                <th style="width: 12%;">Drop-off</th>
                                <th style="width: 38%;">Warnings</th>
                                <th style="width: 18%;">{{ tab === 'open' ? 'Actions' : 'Log' }}</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-if="loading">
                                <td colspan="6" class="text-center py-5 text-muted">Loading...</td>
                            </tr>
                            <tr v-else-if="!rows.length">
                                <td colspan="6" class="text-center py-5 text-muted">
                                    No {{ tab }} warnings match the selected filters.
                                </td>
                            </tr>
                            <tr v-for="row in rows" :key="row.booking_id">
                                <td>
                                    <a :href="`/bookings/${row.booking_id}/edit`" target="_blank" rel="noopener">
                                        {{ row.booking_id }}
                                    </a>
                                    <div v-if="row.warnings.length > 1" class="text-muted" style="font-size: 0.75rem;">
                                        {{ row.resolved_count + row.ignored_count }}/{{ row.warnings.length }} resolved
                                    </div>
                                </td>
                                <td>{{ row.client }}</td>
                                <td>
                                    <span class="badge badge-info">{{ typeName(row.booking_type) }}</span>
                                </td>
                                <td>{{ row.dropoff_end | dropoffDate }}</td>
                                <td>
                                    <div v-for="warning in row.warnings" :key="warning.id"
                                         class="mb-2 px-2 py-1 rounded"
                                         :class="warningClass(warning)">
                                        <div class="text-uppercase font-weight-bold" style="font-size: 0.7rem;">
                                            {{ ruleName(warning.rule_id) }}
                                        </div>
                                        <div style="font-size: 0.85rem;">{{ warning.message }}</div>
                                    </div>
                                </td>
                                <td>
                                    <div v-for="warning in row.warnings" :key="warning.id" class="mb-2">
                                        <template v-if="warning.status === 1">
                                            <div v-if="ignoringId !== warning.id">
                                                <button class="btn btn-sm btn-danger" @click="startIgnore(warning)">
                                                    Ignore
                                                </button>
                                                <div class="text-muted" style="font-size: 0.7rem;">
                                                    Resolved by editing the booking.
                                                </div>
                                            </div>
                                            <div v-else>
                                                <select class="form-control form-control-sm mb-1" v-model="ignoreReason">
                                                    <option :value="''" disabled>Select a reason (required)</option>
                                                    <option v-for="reason in ignoreReasons" :key="reason" :value="reason">
                                                        {{ reason }}
                                                    </option>
                                                </select>
                                                <button class="btn btn-sm btn-danger mr-1"
                                                        :disabled="!ignoreReason"
                                                        @click="confirmIgnore(warning)">
                                                    Confirm
                                                </button>
                                                <button class="btn btn-sm btn-outline-secondary" @click="cancelIgnore">
                                                    Cancel
                                                </button>
                                            </div>
                                        </template>
                                        <template v-else-if="warning.status === 2">
                                            <span class="badge badge-success">
                                                Resolved {{ warning.resolved_by ? 'by ' + warning.resolved_by : 'automatically' }}
                                            </span>
                                            <div class="text-muted" style="font-size: 0.7rem;">{{ warning.resolved_at }}</div>
                                            <div v-if="warning.resolution" class="text-muted font-italic" style="font-size: 0.7rem;">
                                                "{{ warning.resolution }}"
                                            </div>
                                        </template>
                                        <template v-else>
                                            <span class="badge badge-warning">Ignored by {{ warning.ignored_by }}</span>
                                            <div class="text-muted" style="font-size: 0.7rem;">{{ warning.ignored_at }}</div>
                                            <div class="text-muted font-italic" style="font-size: 0.7rem;">
                                                "{{ warning.ignore_reason }}"
                                            </div>
                                            <button class="btn btn-link btn-sm p-0" style="font-size: 0.75rem;"
                                                    @click="undoIgnore(warning)">
                                                Undo
                                            </button>
                                        </template>
                                    </div>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <nav v-if="lastPage > 1" class="mt-3">
                    <ul class="pagination">
                        <li class="page-item" :class="{disabled: currentPage === 1}">
                            <button class="page-link" @click="fetchWarnings(currentPage - 1)">Previous</button>
                        </li>
                        <li class="page-item disabled">
                            <span class="page-link">{{ currentPage }} / {{ lastPage }}</span>
                        </li>
                        <li class="page-item" :class="{disabled: currentPage === lastPage}">
                            <button class="page-link" @click="fetchWarnings(currentPage + 1)">Next</button>
                        </li>
                    </ul>
                </nav>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: "CockpitOverview",
    props: {
        ignoreReasons: {
            type: Array,
            required: true,
        },
    },
    data() {
        return {
            tab: 'open',
            rows: [],
            rules: [],
            counters: {open: 0, resolved: 0, ignored: 0, open_warnings: 0},
            ruleFilter: null,
            bookingType: 0,
            search: '',
            loading: false,
            total: 0,
            currentPage: 1,
            lastPage: 1,
            ignoringId: null,
            ignoreReason: '',
            searchTimeout: null,
            requestId: 0,
        };
    },
    computed: {
        kpiCards() {
            return [
                {tab: 'open', label: 'Bookings with open warnings', value: this.counters.open, textClass: 'text-danger'},
                {tab: 'ignored', label: 'Bookings ignored', value: this.counters.ignored, textClass: 'text-warning'},
                {tab: 'resolved', label: 'Bookings resolved', value: this.counters.resolved, textClass: 'text-success'},
                {
                    tab: null,
                    label: 'Active rules',
                    value: this.rules.filter(rule => rule.enabled).length + '/' + this.rules.length,
                    textClass: '',
                },
            ];
        },
    },
    filters: {
        dropoffDate(value) {
            if (!value || value.startsWith('0000')) {
                return '—';
            }
            const [date, time] = value.split(' ');
            const [year, month, day] = date.split('-');
            return `${day}-${month}-${year} ${(time || '').slice(0, 5)}`.trim();
        },
    },
    created() {
        this.fetchRules();
        this.fetchWarnings(1);
    },
    methods: {
        fetchRules() {
            axios.get('/cockpit/rules').then(response => {
                this.rules = response.data.data;
            }).catch(() => {
                this.flashMessage.error({title: 'Error', message: 'Could not load rules.', time: 4000});
            });
        },
        fetchWarnings(page = 1) {
            clearTimeout(this.searchTimeout);
            const requestId = ++this.requestId;
            this.loading = true;
            return axios.get('/cockpit/warnings', {
                params: {
                    tab: this.tab,
                    rule_id: this.ruleFilter || undefined,
                    booking_type: this.bookingType || undefined,
                    search: this.search || undefined,
                    page: page,
                },
            }).then(response => {
                if (requestId !== this.requestId) {
                    return;
                }
                if (response.data.current_page > response.data.last_page) {
                    return this.fetchWarnings(response.data.last_page);
                }
                this.rows = response.data.data;
                this.total = response.data.total;
                this.currentPage = response.data.current_page;
                this.lastPage = response.data.last_page;
                this.counters = response.data.counters;
            }).catch(() => {
                this.flashMessage.error({title: 'Error', message: 'Could not load warnings.', time: 4000});
            }).finally(() => {
                if (requestId === this.requestId) {
                    this.loading = false;
                }
            });
        },
        debouncedFetch() {
            clearTimeout(this.searchTimeout);
            this.searchTimeout = setTimeout(() => this.fetchWarnings(1), 400);
        },
        switchTab(tab) {
            this.tab = tab;
            this.fetchWarnings(1);
        },
        setRuleFilter(ruleId) {
            this.ruleFilter = ruleId;
            this.fetchWarnings(1);
        },
        ruleName(ruleId) {
            const rule = this.rules.find(rule => rule.id === ruleId);
            return rule ? rule.name : '';
        },
        typeName(type) {
            return type === 4 ? 'Ad-hoc' : (type === 3 ? 'Flex' : type);
        },
        warningClass(warning) {
            if (warning.status === 2) {
                return 'alert-success';
            }
            if (warning.status === 3) {
                return 'alert-warning';
            }
            return 'alert-danger';
        },
        startIgnore(warning) {
            this.ignoringId = warning.id;
            this.ignoreReason = '';
        },
        cancelIgnore() {
            this.ignoringId = null;
            this.ignoreReason = '';
        },
        confirmIgnore(warning) {
            axios.post(`/cockpit/warnings/${warning.id}/ignore`, {reason: this.ignoreReason}).then(() => {
                this.flashMessage.success({title: 'Success', message: 'Warning ignored.', time: 3000});
                this.cancelIgnore();
                this.fetchWarnings(this.currentPage);
                this.fetchRules();
            }).catch(error => {
                this.flashMessage.error({
                    title: 'Error',
                    message: error.response?.data?.message || 'Could not ignore the warning.',
                    time: 4000,
                });
            });
        },
        undoIgnore(warning) {
            axios.post(`/cockpit/warnings/${warning.id}/unignore`).then(() => {
                this.flashMessage.success({title: 'Success', message: 'Warning re-opened.', time: 3000});
                this.fetchWarnings(this.currentPage);
                this.fetchRules();
            }).catch(error => {
                this.flashMessage.error({
                    title: 'Error',
                    message: error.response?.data?.message || 'Could not re-open the warning.',
                    time: 4000,
                });
            });
        },
    },
};
</script>
